import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

function FilterStockBuy({ onFilterChange, products }) {
    const [filters, setFilters] = useState({
        brand: '',
        productType: '',
        priceRange: { min: '', max: '' },
        vendor: '',
        searchText: ''
    });

    const [brands, setBrands] = useState([]);
    const [productTypes, setProductTypes] = useState([]);
    const [vendors, setVendors] = useState([]);

    // Extract unique values from products for filter options
    useEffect(() => {
        if (products && products.length > 0) {
            const uniqueBrands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort();
            const uniqueTypes = [...new Set(products.map(p => p.productType).filter(Boolean))].sort();
            const uniqueVendors = [...new Set(products.map(p => p.vendor).filter(Boolean))].sort();

            setBrands(uniqueBrands);
            setProductTypes(uniqueTypes);
            setVendors(uniqueVendors);
        }
    }, [products]);

    const handleFilterChange = (field, value) => {
        const newFilters = { ...filters, [field]: value };
        setFilters(newFilters);
        onFilterChange(newFilters);
    };

    const handlePriceRangeChange = (type, value) => {
        const newPriceRange = { ...filters.priceRange, [type]: value };
        const newFilters = { ...filters, priceRange: newPriceRange };
        setFilters(newFilters);

        // Validate price range
        if (newPriceRange.min && newPriceRange.max) {
            const min = parseFloat(newPriceRange.min);
            const max = parseFloat(newPriceRange.max);
            if (min > max) {
                toast.error('Minimum price cannot be greater than maximum price');
                return;
            }
        }

        onFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            brand: '',
            productType: '',
            priceRange: { min: '', max: '' },
            vendor: '',
            searchText: ''
        };
        setFilters(clearedFilters);
        onFilterChange(clearedFilters);
        toast.success('Filters cleared');
    };

    const hasActiveFilters = filters.brand || filters.productType ||
                             filters.priceRange.min || filters.priceRange.max ||
                             filters.vendor || filters.searchText;

    return (
        <div className="bg-white h-full overflow-y-auto">
            {/* Filter Header */}
            <div className="p-5 border-b border-gray-200 sticky top-0 bg-gradient-to-r from-indigo-50 to-purple-50 z-10 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                        <h3 className="text-lg font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent uppercase tracking-tight">Filters</h3>
                    </div>
                    {hasActiveFilters && (
                        <button
                            onClick={handleClearFilters}
                            className="text-xs bg-gradient-to-r from-pink-500 to-rose-500 text-white px-3 py-1 rounded-full hover:from-pink-600 hover:to-rose-600 font-semibold uppercase tracking-wide transition-all shadow-md hover:shadow-lg"
                        >
                            Clear
                        </button>
                    )}
                </div>

                {/* Search Text */}
                <div className="relative">
                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        value={filters.searchText}
                        onChange={(e) => handleFilterChange('searchText', e.target.value)}
                        placeholder="Search products..."
                        className="w-full pl-10 pr-3 py-2.5 border-2 border-indigo-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm transition-all bg-white"
                    />
                </div>
            </div>

            {/* Filter Content */}
            <div className="p-5">
                {/* Brand Filter */}
                <div className="mb-6">
                    <h4 className="text-xs font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-3 uppercase tracking-wider flex items-center gap-2">
                        <span className="h-1 w-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"></span>
                        Brand
                    </h4>
                    <div className="space-y-1 max-h-48 overflow-y-auto custom-scrollbar">
                        <label className="flex items-center cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 p-2.5 rounded-lg transition-all group">
                            <input
                                type="radio"
                                name="brand"
                                checked={filters.brand === ''}
                                onChange={() => handleFilterChange('brand', '')}
                                className="mr-3 w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-700 group-hover:text-indigo-700 font-medium">All Brands</span>
                        </label>
                        {brands.map(brand => (
                            <label key={brand} className="flex items-center cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 p-2.5 rounded-lg transition-all group">
                                <input
                                    type="radio"
                                    name="brand"
                                    checked={filters.brand === brand}
                                    onChange={() => handleFilterChange('brand', brand)}
                                    className="mr-3 w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="text-sm text-gray-700 group-hover:text-indigo-700">{brand}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Product Type Filter */}
                <div className="mb-6 pb-6 border-b border-gray-200">
                    <h4 className="text-xs font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
                        <span className="h-1 w-1 bg-black rounded-full"></span>
                        Product Type
                    </h4>
                    <div className="space-y-1 max-h-48 overflow-y-auto custom-scrollbar">
                        <label className="flex items-center cursor-pointer hover:bg-blue-50 p-2.5 rounded-lg transition-colors group">
                            <input
                                type="radio"
                                name="productType"
                                checked={filters.productType === ''}
                                onChange={() => handleFilterChange('productType', '')}
                                className="mr-3 w-4 h-4 text-black focus:ring-black"
                            />
                            <span className="text-sm text-gray-700 group-hover:text-gray-900 font-medium">All Types</span>
                        </label>
                        {productTypes.map(type => (
                            <label key={type} className="flex items-center cursor-pointer hover:bg-blue-50 p-2.5 rounded-lg transition-colors group">
                                <input
                                    type="radio"
                                    name="productType"
                                    checked={filters.productType === type}
                                    onChange={() => handleFilterChange('productType', type)}
                                    className="mr-3 w-4 h-4 text-black focus:ring-black"
                                />
                                <span className="text-sm text-gray-700 group-hover:text-gray-900">{type}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Vendor Filter */}
                <div className="mb-6 pb-6 border-b border-gray-200">
                    <h4 className="text-xs font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
                        <span className="h-1 w-1 bg-black rounded-full"></span>
                        Usage Type
                    </h4>
                    <div className="space-y-1">
                        <label className="flex items-center cursor-pointer hover:bg-blue-50 p-2.5 rounded-lg transition-colors group">
                            <input
                                type="radio"
                                name="vendor"
                                checked={filters.vendor === ''}
                                onChange={() => handleFilterChange('vendor', '')}
                                className="mr-3 w-4 h-4 text-black focus:ring-black"
                            />
                            <span className="text-sm text-gray-700 group-hover:text-gray-900 font-medium">All Types</span>
                        </label>
                        {vendors.map(vendor => (
                            <label key={vendor} className="flex items-center cursor-pointer hover:bg-blue-50 p-2.5 rounded-lg transition-colors group">
                                <input
                                    type="radio"
                                    name="vendor"
                                    checked={filters.vendor === vendor}
                                    onChange={() => handleFilterChange('vendor', vendor)}
                                    className="mr-3 w-4 h-4 text-black focus:ring-black"
                                />
                                <span className="text-sm text-gray-700 group-hover:text-gray-900">{vendor}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Price Range Filter */}
                <div className="mb-6">
                    <h4 className="text-xs font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
                        <span className="h-1 w-1 bg-black rounded-full"></span>
                        Price Range
                    </h4>
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-2">Minimum</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                                <input
                                    type="number"
                                    value={filters.priceRange.min}
                                    onChange={(e) => handlePriceRangeChange('min', e.target.value)}
                                    placeholder="0"
                                    className="w-full pl-8 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-2">Maximum</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                                <input
                                    type="number"
                                    value={filters.priceRange.max}
                                    onChange={(e) => handlePriceRangeChange('max', e.target.value)}
                                    placeholder="Any"
                                    className="w-full pl-8 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FilterStockBuy;
